import {useLocation, useNavigate} from "react-router-dom";
import {Center, CloseButton, Container, Group, Loader, Paper, Space, Title} from "@mantine/core";
import {
  buildPatchDto,
  PatchDTO,
  SERVICE_TYPE_PATCH_FIELD_MAP,
  ServiceType,
  serviceTypeSelector,
  useGetServiceTypeQuery
} from "@ots-frontend/ots-data";
import {useTranslation} from "react-i18next";
import {useCallback, useMemo, useState} from "react";
import {skipToken} from "@reduxjs/toolkit/query";
import {ServiceTypeForm, ServiceTypeFormValues} from "../../components/form/ServiceTypeForm/ServiceTypeForm";
import {useServiceTypeMutation} from "../../hooks/useServiceTypeMutation";
import {useApp} from "../../providers/RegionProvider/RegionProvider";
import {ProtectedPage} from "../../components/page/ProtectedPage/ProtectedPage";
import {toFormDefaultsFromServiceType} from "../../components/form/ServiceTypeForm/useServiceTypeForm";

type Props = {
  id?: string;
  copy?: boolean;
  formDefaults?: Partial<ServiceTypeFormValues>
}

export const ServiceTypeFormPageRoute = () => {
  const {state} = useLocation();
  const id = state?.id;
  const copy = state?.copy;
  const formDefaults = state?.formDefaults;
  return (
      <ServiceTypeFormPage
          id={id}
          copy={copy}
          formDefaults={formDefaults}
      />
  );
};

export const ServiceTypeFormPage = (props: Props) => {
  const {id, copy = false, formDefaults} = props;

  const {regionId} = useApp()

  const navigate = useNavigate();
  const {t} = useTranslation(["common"]);

  const [access, setAccess] = useState(false);

  const selectServiceType = useMemo(() => {
    return serviceTypeSelector()
  }, [])

  const {currServiceType, isLoading} = useGetServiceTypeQuery(access ? (id ?? skipToken) : skipToken, {
    selectFromResult: (result) => ({
      currServiceType: selectServiceType(result),
      isLoading: result.isLoading
    })
  });

  const _formDefaults: Partial<ServiceTypeFormValues> = useMemo(() => {
    return formDefaults ? formDefaults : toFormDefaultsFromServiceType(currServiceType);
  }, [currServiceType, formDefaults]);

  const isEdit = useMemo(() => {
    return currServiceType && !copy
  }, [currServiceType, copy])

  const title: string = useMemo(() => {
    if (isEdit) {
      return t("page.entity.servicetype.form.title.edit", "Edit Service Type", {ns: "common"});
    } else {
      return t("page.entity.servicetype.form.title.new", "New Service Type", {ns: "common"});
    }
  }, [isEdit, t]);

  const {
    createServiceType,
    patchUpdateServiceType,
    isCreating,
    isUpdating,
  } = useServiceTypeMutation()

  const onServiceTypeSubmit = useCallback((formValues: ServiceTypeFormValues) => {
    const patch: Partial<ServiceType> = {
      type: formValues.type,
      description: formValues.description,
      abbr: formValues.abbr,
      doNotUse: formValues.doNotUse,
    }

    if (isEdit) {
      const dto: PatchDTO<any> = buildPatchDto(patch, SERVICE_TYPE_PATCH_FIELD_MAP)
      patchUpdateServiceType(currServiceType.id, dto, {onSuccess: () => navigate(-1)})
    } else {
      const serviceType: Partial<ServiceType> = {
        ...patch,
      };
      createServiceType(serviceType, {onSuccess: (payload) => navigate(`/service-types/profile?id=${payload.id}`, {replace: true})})
    }
  }, [currServiceType, isEdit, patchUpdateServiceType, navigate, createServiceType]);

  return (
      <ProtectedPage
          entity={currServiceType}
          permissions={[isEdit ? "servicetype_edit" : "servicetype_create"]}
          isLoading={isLoading}
          onAccess={setAccess}>
        {(classes) => (
            <Container className={classes.container} fluid={true} py="md">
              <Center>
                {isLoading && <Loader/>}
                {!isLoading && (
                    <Paper miw={500} shadow="sm" p="md" py="md">
                      <Group position="apart">
                        <Title align="center">
                          {title}
                        </Title>
                        <CloseButton title="Close" size="xl" iconSize={20} onClick={() => navigate(-1)}/>
                      </Group>
                      <Space h="md"/>
                      <ServiceTypeForm
                          options={{
                            regionId: regionId,
                          }}
                          defaultValues={_formDefaults}
                          submitting={isCreating || isUpdating}
                          onSubmit={onServiceTypeSubmit}/>
                    </Paper>
                )}
              </Center>
            </Container>
        )}
      </ProtectedPage>
  );
};

